import {
  setUsageLimitDefaultsApiV1InternalConfigurationUsageLimitDefaultsPut,
  usageLimitDefaultsApiV1InternalConfigurationUsageLimitDefaultsGet,
  type AdminUsageLimitDefaults,
} from '@closet-theory/api';
import { useCallback, useEffect, useState } from 'react';
import { Button } from '@ui/components/base/buttons/button';
import { Callout, Code, FailureText, Section } from '@ui/console/primitives';
import { TextField } from '@ui/console/forms';
import { EntityRef } from './references';
import { Actions } from './components';
import { configureAdminApi, handleUnauthorized } from './auth';
import { errorDetail } from './operation-policy';
import { parseLimit, WRITABLE_LIMITS, type WritableLimit } from './usage-limit-fields';

function isWritableLimit(value: unknown): value is WritableLimit {
  return (
    value === null ||
    value === 'unlimited' ||
    (typeof value === 'number' && Number.isSafeInteger(value) && value >= 0)
  );
}

/** Preserve an unexpected read value for the operator instead of interpreting it. */
export function displayLimit(value: unknown): string {
  if (value === null) return 'default';
  if (value === 'unlimited' || typeof value === 'number') return String(value);
  if (typeof value === 'string') return value;
  if (value === undefined) return 'undefined';
  try {
    return JSON.stringify(value) ?? `unrenderable ${typeof value}`;
  } catch {
    return `unrenderable ${typeof value}`;
  }
}

type SaveState = 'idle' | 'saving' | 'saved' | 'failed';

function DefaultLimitControl({
  limitKey,
  label,
  period,
  present,
  serverValue,
  fallback,
  onSaved,
}: {
  limitKey: string;
  label: string;
  period: string;
  present: boolean;
  serverValue: unknown;
  /** What the product uses when nothing at all is set: the built-in number. */
  fallback: number | undefined;
  onSaved: (saved: AdminUsageLimitDefaults) => void;
}) {
  const readable = !present || isWritableLimit(serverValue);
  const displayedServerValue = present ? displayLimit(serverValue) : 'default';
  const [value, setValue] = useState(displayedServerValue);
  const [state, setState] = useState<SaveState>('idle');
  const [message, setMessage] = useState<string>();

  useEffect(() => {
    setValue(displayedServerValue);
  }, [displayedServerValue]);

  const commit = async () => {
    if (!readable || state === 'saving' || value === displayedServerValue) return;
    const parsed = parseLimit(value);
    if (!parsed.ok) {
      setState('failed');
      setMessage('A whole number, “unlimited”, or “default”.');
      return;
    }

    setState('saving');
    setMessage(undefined);
    configureAdminApi();
    const result = await setUsageLimitDefaultsApiV1InternalConfigurationUsageLimitDefaultsPut({
      body: {
        limits: { [limitKey]: parsed.value },
        reason: `Set ${limitKey} from the fleet-wide admin console.`,
      },
    });
    handleUnauthorized(result.response.status);
    if (result.error || !result.data) {
      setValue(displayedServerValue);
      setState('failed');
      setMessage(errorDetail(result.error, 'That fleet-wide limit could not be saved.'));
      return;
    }
    setState('saved');
    onSaved(result.data);
  };

  const guidance = !readable
    ? 'Disabled because the current stored value could not be read safely.'
    : `Type a whole number, “unlimited”, or “default”${
        fallback === undefined ? '' : ` (the built-in ${fallback})`
      }; changes save on blur or Enter.`;

  return (
    <div className="flex flex-col gap-1">
      <TextField
        label={`${label} ${period}`}
        value={value}
        disabled={!readable || state === 'saving'}
        hint={guidance}
        onChange={(event) => {
          setValue(event.target.value);
          setState('idle');
          setMessage(undefined);
        }}
        onBlur={() => void commit()}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            void commit();
          }
        }}
      />
      {state === 'saving' ? <span role="status">Saving {label.toLowerCase()}…</span> : null}
      {state === 'saved' ? <span role="status">Saved {label.toLowerCase()}.</span> : null}
      {state === 'failed' && message ? <FailureText raw={message} /> : null}
    </div>
  );
}

export function UsageLimitDefaults() {
  const [data, setData] = useState<AdminUsageLimitDefaults>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();

  const load = useCallback(async () => {
    setLoading(true);
    setError(undefined);
    configureAdminApi();
    const result = await usageLimitDefaultsApiV1InternalConfigurationUsageLimitDefaultsGet();
    handleUnauthorized(result.response.status);
    setLoading(false);
    if (result.error || !result.data) {
      setError(errorDetail(result.error, 'Fleet-wide usage limit defaults could not be loaded.'));
      return;
    }
    setData(result.data);
  }, []);

  useEffect(() => void load(), [load]);

  const limits = data?.limits ?? {};
  const knownKeys = new Set(WRITABLE_LIMITS.map(({ key }) => key));
  const unexpectedKeys = Object.keys(limits).filter((key) => !knownKeys.has(key));

  return (
    <Section
      title="Fleet-wide usage limit defaults"
      description="The fallback inherited by every member who has no explicit per-member override. Each limit saves independently when you leave its field or press Enter."
      actions={
        <Actions>
          <Button size="sm" color="secondary" isDisabled={loading} onClick={() => void load()}>
            {loading ? 'Refreshing…' : 'Refresh'}
          </Button>
        </Actions>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? <Callout tone="error" title={error} /> : null}
        {!loading && data && !data.configured ? (
          <Callout tone="neutral" title="No fleet-wide defaults have ever been configured.">
            There is no stored defaults row. This is different from a configured row whose limits
            are deliberately empty. Saving any limit below creates the row.
          </Callout>
        ) : null}
        {!loading && data?.configured && Object.keys(limits).length === 0 ? (
          <Callout tone="info" title="Fleet-wide defaults are configured with empty limits.">
            A defaults row exists, but it stores no limit values. This is not the never-configured
            state.
          </Callout>
        ) : null}
        {data ? (
          <div className="flex flex-col gap-4 rounded-xl border border-secondary bg-primary p-4">
            {WRITABLE_LIMITS.map(({ key, label, period }) => (
              <DefaultLimitControl
                key={key}
                limitKey={key}
                label={label}
                period={period}
                present={Object.hasOwn(limits, key)}
                serverValue={limits[key]}
                fallback={data.fallback_limits?.[key]}
                onSaved={setData}
              />
            ))}
            {unexpectedKeys.map((key) => (
              <div
                key={key}
                className="flex flex-wrap items-baseline justify-between gap-2 text-sm"
              >
                <span className="text-secondary">
                  <Code>{key}</Code>
                </span>
                <span className="text-error-primary">
                  <Code>{displayLimit(limits[key])}</Code> — unrecognized key, shown as stored
                </span>
              </div>
            ))}
          </div>
        ) : null}
        {data?.updated_at || data?.updated_by ? (
          <div className="flex flex-col gap-2 rounded-xl border border-secondary bg-secondary p-4">
            {data.updated_by ? (
              <span className="flex min-w-0 flex-col gap-0.5">
                <span className="text-xs text-tertiary">Last changed by</span>
                <EntityRef kind="staff" id={data.updated_by} />
              </span>
            ) : null}
            {data.updated_at ? (
              <span className="flex min-w-0 flex-col gap-0.5">
                <span className="text-xs text-tertiary">Last changed at</span>
                <span className="text-sm text-secondary">
                  {new Date(data.updated_at).toLocaleString()}
                </span>
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
    </Section>
  );
}
